import { mapMutations } from "vuex";
import { isFeatureEnabled, payrollImportMode } from "~/utils/features-enabled";
import { FEATURE_MENU_PREFIX } from "~/utils/feature-menu-prefix";
import { isPayrollImportLogLinkHidden } from "~/utils/payroll-import-log";

/**
 * @typedef {Object} MenuTree
 * @property {number} ID
 * @property {string} CreatedAt
 * @property {string} UpdatedAt
 * @property {?string} DeletedAt
 * @property {string} color
 */

/**
 * @typedef {Object} Item
 * @property {number} ID
 * @property {string} CreatedAt
 * @property {string} UpdatedAt
 * @property {?string} DeletedAt
 * @property {string} title
 * @property {string} icon
 * @property {string} link
 * @property {string} color
 * @property {number} order
 * @property {MenuTree} MenuTree
 * @property {number} MenuTreeID
 * @property {boolean} setting
 */

export const base = {
	data() {
		return {
			publicPath: this.$tenantConfig?.api?.publicFolder || "",
			filterTag: null,
			/** @type {Item[]} */
			item: null,
			mini: true,
			adminActive: null,
			admin: [],
			menu: [],
			expanseMenus: [],
			drawerExpanseMenus: [],
		};
	},
	methods: {
		pushRoute(link) {
			let push =
				link === "dashboard"
					? { name: "dashboard" }
					: { name: "dashboard-" + link.replace(/\//g, "-") };
			this.$router.push(push);
		},
		/**
		 * Converts a menu tree title into the URL slug used as link prefix
		 * by its forms. Matches the convention encoded in the API seeder:
		 * lowercase, accent-stripped, spaces become hyphens.
		 */
		slugifyMenuTitle(title) {
			if (!title) return "";
			// Strip Unicode combining marks (accents) to produce ASCII-only slugs.
			return title
				.normalize("NFD")
				.replace(/[̀-ͯ]/g, "")
				.toLowerCase()
				.replace(/\s+/g, "-")
				.replace(/[^a-z0-9-]/g, "");
		},
		navigateToGroup(root) {
			this.$router.push(`/dashboard/${this.slugifyMenuTitle(root.title)}`);
		},
		navigateToSubGroup(root, sub) {
			const rootSlug = this.slugifyMenuTitle(root.title);
			const subSlug = this.slugifyMenuTitle(sub.title);
			this.$router.push(`/dashboard/${rootSlug}/${subSlug}`);
		},
		...mapMutations({
			drawer: "base/drawer",
		}),
	},
	computed: {
		getLevel() {
			return this.$store.getters["loggedInUser"].level;
		},
		getDrawer: {
			get() {
				return this.$store.getters["base/drawer"];
			},
			set(val) {
				// this.drawer(val)
			},
		},
		getMenu() {
			return this.menu;
		},
		getExpanseMenu() {
			return this.expanseMenus;
		},
		getDrawerExpanseMenus() {
			return this.drawerExpanseMenus;
		},
		getAdmin() {
			return this.admin;
		},
		getSettingsMenuModel: {
			get() {
				if (this.item != null) return null;
				else return this.adminActive;
			},
			set(val) {
				this.item = null;
				this.adminActive = val;
			},
		},
		getMenuModel: {
			get() {
				if (this.adminActive != null) return null;
				else return this.item;
			},
			set(val) {
				this.item = val;
				this.adminActive = null;
			},
		},
		items() {
			return this.$store.getters["privileges"];
		},
		/**
		 * Builds the hierarchical menu tree from the user's privileges.
		 *
		 * Returns an ordered array of root menu trees. Each root has:
		 *   { ID, title, icon, color, order, items[], children[] }
		 * Where:
		 *   - items: forms attached directly to the root (no sub-group).
		 *   - children: sub-groups, each with the same shape minus `children`
		 *               (the model is limited to two levels).
		 *
		 * A root appears in the tree whenever any descendant form is visible
		 * to the user, even if the root has no direct forms of its own.
		 */
		getMenuTree() {
			// Gating por feature: con un módulo opcional off ocultamos sus páginas del
			// menú por prefijo de Link, aunque sus filas sigan sembradas en la BD (mismo
			// criterio que el ocultado por servicio). El seed limpio ya no las crea; este
			// filtro cubre además tenants ya sembrados (mapa único en
			// utils/feature-menu-prefix). SII -> facturacion/*; firmas -> firma/*.
			// El log de importación de nóminas depende del modo (no es booleano): se
			// oculta con features.payrollImport.mode = off (utils/payroll-import-log).
			const payrollMode = payrollImportMode();
			const hiddenByFeature = (item) => {
				if (typeof item.link !== "string") return false;
				if (isPayrollImportLogLinkHidden(item.link, payrollMode)) return true;
				return Object.entries(FEATURE_MENU_PREFIX).some(
					([feature, prefix]) => item.link.startsWith(prefix) && !isFeatureEnabled(feature),
				);
			};
			const items = (this.items || []).filter((item) => !hiddenByFeature(item));
			const roots = new Map();

			const ensureRoot = (rootMT) => {
				if (!rootMT || !rootMT.ID) return null;
				if (!roots.has(rootMT.ID)) {
					roots.set(rootMT.ID, {
						ID: rootMT.ID,
						title: rootMT.title,
						icon: rootMT.icon,
						color: rootMT.color,
						order: rootMT.order || 0,
						items: [],
						children: [],
					});
				}
				return roots.get(rootMT.ID);
			};

			const ensureSubGroup = (subMT) => {
				const root = ensureRoot(subMT.Parent);
				if (!root) return null;
				let existing = root.children.find((c) => c.ID === subMT.ID);
				if (!existing) {
					existing = {
						ID: subMT.ID,
						title: subMT.title,
						icon: subMT.icon,
						color: subMT.color,
						order: subMT.order || 0,
						items: [],
					};
					root.children.push(existing);
				}
				return existing;
			};

			for (const item of items) {
				const mt = item.MenuTree;
				if (!mt || !mt.ID) continue;
				if (mt.parent_id) {
					const sub = ensureSubGroup(mt);
					if (sub) sub.items.push(item);
				} else {
					const root = ensureRoot(mt);
					if (root) root.items.push(item);
				}
			}

			const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
			const result = Array.from(roots.values());
			result.forEach((r) => {
				r.items.sort(byOrder);
				r.children.sort(byOrder);
				r.children.forEach((c) => c.items.sort(byOrder));
			});
			result.sort(byOrder);
			return result;
		},
	},
};
